import React from "react";

// region External libraries
import { format } from "date-fns";
// endregion External libraries
// region Hooks
import { useExposure } from "@hooks/useExposure";
// endregion Hooks
// region Languages
import useTranslation from "@languages/useTranslation";
import { AnnouncementMessages } from "@languages/interfaces";
// endregion Languages
// region Store
import { MyAnnouncement } from "src/store/graphql/groups/announcement/query.graphql";
// endregion Store
// region Styles
import * as Styled from "./styles";
// endregion Styles

// region Interfaces
interface NotificationItemProps {
  item: MyAnnouncement;
  onRead: (id: string) => void;
  /** Called when the row has been on the user's screen long enough to count as read. */
  onExposed: (id: string) => void;
  onCtaClick: (item: MyAnnouncement) => void;
}
// endregion Interfaces

/**
 * One announcement row, shared by the bell dropdown and the "view all" dialog.
 * It marks itself as read once it has actually been on screen for a while, so
 * reading the text in the list already counts; clicking the row marks it
 * immediately. The CTA button (shown when the announcement has a link)
 * registers the click and opens the link.
 */
const NotificationItem: React.FC<NotificationItemProps> = ({ item, onRead, onExposed, onCtaClick }) => {

  const { t } = useTranslation();

  const isUnread = !item.readAt;

  const exposureRef = useExposure<HTMLDivElement>(isUnread, () => onExposed(item.id));

  const handleItemClick = () => {
    if (isUnread) onRead(item.id);
  };

  const handleCtaClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onCtaClick(item);
  };

  return (
    <Styled.Item ref={exposureRef} $unread={isUnread} onClick={handleItemClick}>
      <Styled.UnreadDot $visible={isUnread} />
      <Styled.Content>
        <Styled.TitleRow>
          <Styled.Title $unread={isUnread}>{item.title}</Styled.Title>
          <Styled.Time>{format(new Date(item.createdAt), "dd/MM/yyyy")}</Styled.Time>
        </Styled.TitleRow>
        <Styled.Message $unread={isUnread}>{item.message}</Styled.Message>
        {item.link && (
          <Styled.CtaRow>
            <Styled.CtaButton type="button" onClick={handleCtaClick}>
              {item.linkLabel || t(AnnouncementMessages.defaultCta)}
            </Styled.CtaButton>
          </Styled.CtaRow>
        )}
      </Styled.Content>
    </Styled.Item>
  );
};

export default NotificationItem;
